import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../../data/notification";
import useT from "../../i18n/useT";
import core from "../../core";

const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const statusStyle = (s) => {
    if (s === "matched") return { background: "#34c38f", color: "#fff" };
    if (s === "ignored") return { background: "#6c757d", color: "#fff" };
    return { background: "#ff9f0a", color: "#fff" };
};

const BankReconcile = () => {
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isStaff = role === "admin" || role === "moderator";

    const [statements, setStatements] = useState([]);
    const [updates, setUpdates] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [openId, setOpenId] = useState(null);
    const [detail, setDetail] = useState(null);

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    useEffect(() => {
        if (!isStaff) return;
        axios.get("/api/bank/statements").then((res) => setStatements(res.data.data || [])).catch(() => {});
    }, [isStaff, updates]);

    useEffect(() => {
        if (openId == null) { setDetail(null); return; }
        axios.get(`/api/bank/statements/${openId}`).then((res) => setDetail(res.data.data)).catch(() => {});
    }, [openId, updates]);

    if (!isStaff) {
        return <div className="page-content-main-container"><h2 className="page-title">{t("bank.title")}</h2></div>;
    }

    const onFile = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setUploading(true);
            axios.post("/api/bank/reconcile", { filename: file.name, csv: reader.result })
                .then((res) => {
                    const d = res.data.data;
                    notify(`${res.data.message.en}: ${d.matched_count}/${d.line_count}`, "success-notification");
                    setUpdates((u) => u + 1);
                    setOpenId(d.statement_id);
                })
                .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"))
                .finally(() => setUploading(false));
        };
        reader.readAsText(file);
        e.target.value = "";
    };

    const updateLine = (lineId, payload) => {
        axios.put(`/api/bank/lines/${lineId}`, payload)
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const deleteStatement = (id) => {
        if (!window.confirm(t("bank.confirmDelete"))) return;
        axios.delete(`/api/bank/statements/${id}`)
            .then((res) => { notify(res.data.message.en, "success-notification"); if (openId === id) setOpenId(null); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const tile = (label, value, accent) => (
        <div style={{ flex: "1 1 150px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "14px" }}>
            <div style={{ fontSize: "12px", opacity: 0.65, marginBottom: "4px" }}>{label}</div>
            <div style={{ fontSize: "20px", fontWeight: 700, color: accent || "var(--text-strong)" }}>{value}</div>
        </div>
    );

    // ---- statement detail view ----
    if (openId != null && detail) {
        const s = detail.statement;
        const unmatched = detail.lines.filter((l) => l.status === "unmatched").length;
        return (
            <div className="page-content-main-container">
                <h2 className="page-title">{t("bank.title")}</h2>
                <button className="btn btn-sm btn-outline-secondary" style={{ marginBottom: "14px" }} onClick={() => setOpenId(null)}>← {t("bank.back")}</button>

                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "18px" }}>
                    {tile(t("bank.summaryMatched"), `${s.matched_count}/${s.line_count}`, "#34c38f")}
                    {tile(t("bank.summaryUnmatched"), unmatched, unmatched > 0 ? "#ff9f0a" : "#34c38f")}
                    {tile(t("bank.summaryTotal"), money(s.total_amount))}
                </div>

                <div style={{ overflowX: "auto" }}>
                    <table className="table" style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                <th>{t("bank.date")}</th>
                                <th>{t("bank.amount")}</th>
                                <th>{t("bank.description")}</th>
                                <th>{t("bank.status")}</th>
                                <th>{t("bank.matchedTxn")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.lines.map((l) => (
                                <tr key={l.id}>
                                    <td>{l.txn_date ? core.formdatDate(l.txn_date, "dd-mm-yyyy") : "—"}</td>
                                    <td>{money(l.amount)}</td>
                                    <td>{l.description || "—"}</td>
                                    <td><span style={{ ...statusStyle(l.status), padding: "2px 10px", borderRadius: "12px", fontSize: "12px" }}>{t(`bank.${l.status}`)}</span></td>
                                    <td>{l.matched_transaction_id ? `#${l.matched_transaction_id} · ${l.vin || ""} · ${money(l.paid_amount)}` : "—"}</td>
                                    <td>
                                        <span style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                                            {l.status !== "matched" && (
                                                <select className="form-control" style={{ width: "auto", fontSize: "12px", padding: "2px 6px" }} defaultValue="" onChange={(e) => e.target.value && updateLine(l.id, { matched_transaction_id: e.target.value })}>
                                                    <option value="">{t("bank.selectTxn")}</option>
                                                    {detail.candidates.map((c) => (
                                                        <option key={c.id} value={c.id}>#{c.id} · {money(c.paid_amount)} · {c.vin}</option>
                                                    ))}
                                                </select>
                                            )}
                                            {l.status === "matched" && <button className="btn btn-sm btn-outline-secondary" onClick={() => updateLine(l.id, { status: "unmatched" })}>{t("bank.unmatch")}</button>}
                                            {l.status !== "ignored" && <button className="btn btn-sm btn-outline-secondary" onClick={() => updateLine(l.id, { status: "ignored" })}>{t("bank.markIgnored")}</button>}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // ---- statements list view ----
    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("bank.title")}</h2>

            <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px", marginBottom: "20px", maxWidth: "520px" }}>
                <h3 style={{ fontSize: "15px", marginBottom: "6px" }}>{t("bank.upload")}</h3>
                <div style={{ fontSize: "12px", opacity: 0.65, marginBottom: "12px" }}>{t("bank.uploadHint")}</div>
                <input type="file" accept=".csv,text/csv" onChange={onFile} disabled={uploading} />
                {uploading && <span style={{ marginLeft: "10px", fontSize: "13px", opacity: 0.7 }}>{t("bank.uploading")}</span>}
            </div>

            <h3 style={{ fontSize: "15px", margin: "6px 0 12px" }}>{t("bank.statements")}</h3>
            {statements.length === 0 ? (
                <div style={{ opacity: 0.6 }}>{t("bank.none")}</div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="table" style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                <th>{t("bank.filename")}</th>
                                <th>{t("bank.lines")}</th>
                                <th>{t("bank.matched")}</th>
                                <th>{t("bank.total")}</th>
                                <th>{t("bank.uploadedBy")}</th>
                                <th>{t("bank.uploadedAt")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {statements.map((s) => (
                                <tr key={s.id}>
                                    <td>{s.filename}</td>
                                    <td>{s.line_count}</td>
                                    <td>{s.matched_count}/{s.line_count}</td>
                                    <td>{money(s.total_amount)}</td>
                                    <td>{s.uploaded_by || "—"}</td>
                                    <td>{s.uploaded_at ? core.formdatDate(s.uploaded_at, "dd-mm-yyyy") : "—"}</td>
                                    <td>
                                        <span style={{ display: "flex", gap: "6px" }}>
                                            <button className="btn btn-sm btn-primary" onClick={() => setOpenId(s.id)}>{t("bank.open")}</button>
                                            <button className="btn btn-sm btn-danger" onClick={() => deleteStatement(s.id)}>{t("bank.delete")}</button>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default BankReconcile;
